import { Dictionary } from "./en";

// Neutral, professional Spanish. Product names, commands and literal tool output stay as they are.
export const es: Dictionary = {
  header: {
    nav: {
      home: "Inicio",
      about: "Sobre mí",
      work: "Proyectos",
      caseStudy: "Caso de estudio",
      contact: "Contacto",
      cv: "CV",
    },
    openMenu: "Abrir menú",
    closeMenu: "Cerrar menú",
    switchLanguageLabel: "Switch to English",
    toggleTheme: "Cambiar tema claro",
  },
  hero: {
    eyebrowBefore: "Hola",
    eyebrowAfter: ", soy Darío Condori Humerez",
    headline: "Construyo apps móviles que la gente sí descarga.",
    ledeBefore:
      "Ingeniero móvil senior con más de 9 años en software, más de 6 publicando aplicaciones móviles en App Store y Google Play. También construyo productos web en producción como MobiZ y POS. OK Delivery, la app de delivery que lancé en plena pandemia, superó las",
    ledeStrong: "10 mil descargas",
    ledeAfter: ".",
    contact: "Contacto",
    readCase: "Leer el caso OK Delivery",
    downloadCv: "Descargar CV",
  },
  about: {
    heading: "Sobre mí",
    paragraphs: [
      "Soy Darío, ingeniero de software especializado en móvil: multiplataforma con React Native para iOS y Android, y Android nativo con Kotlin y Jetpack Compose. Elijo una u otra según lo que necesita el producto, no según la que me sé mejor.",
      "En 2020, en plena cuarentena, construí y lancé OK Delivery para que los restaurantes locales pudieran seguir vendiendo. Sigue en producción y ya superó las 10 mil descargas.",
      "Desde entonces he trabajado con startups de Latinoamérica, incluida una SaaS respaldada por Y Combinator. Diseñé la sincronización offline-first para trabajadores de campo sin conexión, escribí un módulo nativo en Kotlin para integrar el SDK de una terminal de pagos en React Native, llevé una migración de JavaScript a TypeScript sin detener los lanzamientos y publiqué asistentes de IA dentro de la app respaldados por servicios en Python.",
    ],
    skills: [
      {
        term: "Móvil",
        value:
          "React Native, Expo (EAS), Android nativo con Kotlin y Jetpack Compose, módulos nativos en Kotlin y Swift, sincronización offline-first, notificaciones push, publicación en tiendas",
      },
      {
        term: "Frontend",
        value: "React, TypeScript, Next.js, Redux Toolkit, React Query, GraphQL, Tailwind CSS",
      },
      {
        term: "Backend e IA",
        value:
          "Node.js, Python (FastAPI, Django), Firebase, Supabase, PostgreSQL, MongoDB, OpenAI API",
      },
      {
        term: "Prácticas",
        value: "Arquitectura limpia, Jest y Testing Library, CI/CD, Sentry, Git Flow",
      },
    ],
    photoAlt: "Darío Condori Humerez",
  },
  portfolio: {
    heading: "Proyectos destacados",
    ossHeading: "Open source",
    ossIntro: "Herramientas que construyo para trabajar mejor con agentes de IA que programan.",
    sideHeading: "Algunos proyectos realizados",
    copy: "Copiar",
    copied: "Copiado",
    terminalLabel: (title: string) => `Sesión de terminal de ejemplo de ${title}`,
    linkLabels: {
      googlePlay: "Google Play",
      appStore: "App Store",
      github: "GitHub",
      npm: "npm",
      demo: "Ver demo",
      website: "Ver sitio",
      caseStudy: "Leer el caso de estudio",
    },
    linkAria: {
      onPlatform: (title: string, platform: string) => `${title} en ${platform}`,
      demo: (title: string) => `Demo de ${title}`,
      website: (title: string) => `Sitio de ${title}`,
    },
    featured: {
      "ok-delivery": {
        title: "OK Delivery",
        stat: "Android · Google Play",
        description:
          "App de delivery de comida que diseñé, construí y lancé durante la cuarentena de 2020, todavía en producción seis años después. Aumentó los pedidos y ventas del negocio en cerca de 60%. Cliente React Native sobre Firebase con costo de envío según la distancia; las versiones para iOS y web están en desarrollo.",
        mediaAlt:
          "Pantalla de inicio de OK Delivery con restaurantes populares de El Alto, Bolivia",
        facts: [
          { value: "10k+", label: "descargas en Google Play" },
          { value: "~60%", label: "más pedidos y ventas" },
          { value: "2020", label: "en producción desde" },
        ],
      },
      mobiz: {
        title: "MobiZ",
        stat: "En producción · Bolivia",
        description:
          "Mapa en tiempo real de taxis y trufis (taxis compartidos) cerca de ti. El pasajero entra sin crear cuenta, ve qué conductores están disponibles y cuántos asientos libres tienen, y les escribe por WhatsApp con un toque. El conductor activa y desactiva su disponibilidad. Se instala como PWA.",
        mediaAlt:
          "Página de MobiZ con un celular que muestra un mapa en vivo de taxis cercanos",
      },
      "zeconds-pos": {
        title: "POS",
        stat: "En producción · SaaS por suscripción",
        description:
          "Punto de venta que reemplaza el lector de códigos y la caja registradora por un celular. La cámara lee códigos EAN, UPC y QR, cada venta actualiza el inventario y una sola cuenta maneja varias tiendas con roles de administrador y cajero. Funciona en el navegador, sin instalar nada.",
        mediaAlt: "Página del POS con la pantalla de caja en un celular",
      },
    },
    openSource: {
      cdd: {
        title: "CDD — Comprehension-Driven Development",
        meta: ["MIT", "Cero dependencias"],
        description:
          "Un control de comprensión para código escrito por IA. Un hook de pre-commit hace una pregunta puntual sobre la parte más riesgosa de tu diff; lo que no puedes explicar queda marcado como COMPREHENSION_DEBT, y una auditoría en CI bloquea el merge mientras haya deuda abierta.",
        installNote: "Luego usa `cdd check`, `cdd audit` y `cdd report`.",
        terminal: {
          // Literal tool output: identical in every language
          out: "comprehension micro-check · 2 file(s)",
          q: "P: Si Redis se cae, redis.incr(key) lanza una excepción. ¿Qué le pasa al usuario que intenta iniciar sesión?",
          answer: "> el error no está controlado, así que no puede entrar",
          // Literal tool output: identical in every language
          ok: "✓ matches the code — you understand this change",
        },
      },
      "voice-screen-assistant": {
        title: "Voice Screen Assistant",
        meta: ["MIT", "Python 3.9+", "Local primero"],
        description:
          "Un asistente por voz que ve tu pantalla. Hablas mientras trabajas: captura tus monitores, entiende lo que tienes abierto y responde en voz alta como un dev senior sentado a tu lado. Funciona con cualquier LLM, o 100% local con Ollama.\n\nDile “enséñame Kotlin” y cambia a modo instructor: un ejercicio pequeño a la vez, revisado contra el código real que tienes en pantalla. Sin SDKs de ningún proveedor — HTTP plano, así que el modelo, la voz o la transcripción se cambian con dos líneas de configuración.",
        terminal: {
          out: "micrófono → voz a texto → LLM + capturas de tus monitores → texto a voz",
          ok: '"¿Te ayudo con ese correo?"',
        },
      },
    },
    side: {
      "my-shop-online": {
        title: "My Shop Online",
        description:
          "Plataforma donde un negocio arma su tienda online con su marca, sube sus productos y recibe los pedidos por WhatsApp. Cada tienda tiene su propio link, y hay plan gratis y suscripciones de pago.",
      },
      "trading-signals": {
        title: "API de señales de trading de Bitcoin",
        description:
          "API que transmite señales de compra y venta de Bitcoin calculadas con cruces de EMA, servida por WebSocket.",
      },
      "netflix-clone": {
        title: "Clon de Netflix",
        description:
          "Aplicación web responsive hecha con React, TypeScript, styled-components y Webpack.",
      },
    },
  },
  caseStudy: {
    eyebrow: "Caso de estudio",
    title: "OK Delivery: lanzar una app de delivery en plena cuarentena",
    facts: [
      {
        term: "Rol",
        value:
          "Todo, de punta a punta: conseguir los restaurantes, la marca y el logo, el diseño de la interfaz, el desarrollo y las publicaciones",
      },
      { term: "Periodo", value: "2020 a la fecha (en mantenimiento)" },
      { term: "Plataforma", value: "Android, en Google Play. iOS y web en desarrollo" },
      {
        term: "Stack",
        value:
          "React Native, Firebase (Auth, Firestore, Cloud Messaging), Redux Toolkit, Google Maps Platform, panel de administración en Angular",
      },
      {
        term: "Resultado",
        value: "Más de 10 mil descargas · cerca de 60% más pedidos y ventas",
      },
    ],
    problem: {
      heading: "El problema",
      paragraphs: [
        "En 2020 la cuarentena cerró de un día para otro los salones de los restaurantes en El Alto, Bolivia. Los locales todavía tenían cocina y clientes, pero ninguna forma de tomar pedidos a distancia. Construí OK Delivery para resolver eso, y lo hice todo yo: conseguí los primeros restaurantes, diseñé el logo y la interfaz, programé la app y la publiqué.",
      ],
    },
    decisions: {
      heading: "Decisiones técnicas",
      items: [
        {
          claim: "Firebase en lugar de servidores que operar.",
          body: "La autenticación, la base de datos y las notificaciones push corren sobre Firebase, llamadas directamente desde la app, y las actualizaciones del pedido llegan por listeners de Firestore. Trabajando solo, no tener servidores que parchar ni escalar pesaba más que la flexibilidad del backend.",
        },
        {
          claim: "Costo de envío según la distancia real, ajustable sin publicar una versión.",
          body: "El costo sale de la distancia en ruta entre el restaurante y el cliente. La tarifa base y el precio por kilómetro viven en Firestore, así los precios cambian sin esperar la revisión de la tienda.",
        },
        {
          claim: "La lógica de dinero vive en funciones puras con pruebas.",
          body: "Los reportes llegaron a mostrar pedidos con costo de envío en cero, porque el botón de enviar se podía tocar antes de que el costo terminara de calcularse. Moví la regla a una función pura, `canSubmitOrder`, y la cubrí con pruebas junto con el cálculo del envío.",
        },
        {
          claim: "Mantener publicable una app con seis años.",
          body: "Google Play ahora exige soporte de páginas de memoria de 16 KB para Android 15. Eso significó actualizar todo el core en una sola migración: React Native 0.74 a 0.77, el SDK de Firebase 18 a 21 y Redux Toolkit 1 a 2.",
        },
      ],
    },
    result: {
      heading: "Resultado",
      paragraphs: [
        "OK Delivery superó las 10 mil descargas en Google Play y aumentó los pedidos y ventas del negocio en cerca de 60%. Seis años después del lanzamiento sigue en producción.",
      ],
    },
    googlePlayAria: "OK Delivery en Google Play",
    back: "Volver a proyectos destacados",
  },
  contact: {
    heading: "Contacto",
    intro: "Si quieres hablar conmigo, no dudes en llamarme o enviarme un mensaje.",
    emailAlt: "Correo electrónico",
    phoneAlt: "Teléfono",
  },
  form: {
    heading: "Escríbeme desde el formulario",
    emailPlaceholder: "Correo electrónico",
    messagePlaceholder: "Deja tu mensaje",
    emailErrorPrefix: "Correo",
    messageErrorPrefix: "Mensaje",
    submit: "Enviar",
    successToast: "¡Mensaje enviado correctamente!",
    thanks: "¡Gracias por escribirme!",
    backToTop: "Volver arriba",
  },
  footer: {
    madeWith: "Este sitio web fue hecho con",
  },
};
